import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, Link } from "react-router-dom";
import styled from 'styled-components';
import { SG_FETCH_REPORT, SG_UPDATE_REPORT, SG_REGENERATE_REPORT_DIMENSION } from 'constants/actions';

export default function Details() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const report = useSelector((s) => s.action_report || {}, shallowEqual);
  const [retrying, setRetrying] = useState(false);

  console.log('Render Details', { id, report });
  useEffect(() => {
    if (id) dispatch({ type: SG_FETCH_REPORT, payload: id });
  }, [dispatch, id]);

  const handleRetryGeneration = () => {
    if (!window.confirm('Retry report generation? This will reset the status to pending and trigger a new generation job.')) return;
    
    setRetrying(true);
    // Update report status to 'pending' to trigger regeneration
    dispatch({ 
      type: SG_REGENERATE_REPORT_DIMENSION, 
      payload: { 
        id: r.id, 
        status: 'pending' 
      } 
    });
    
    // Refresh after 2 seconds
    setTimeout(() => {
      dispatch({ type: SG_FETCH_REPORT, payload: id });
      setRetrying(false);
    }, 2000);
  };

  const r = report[id] || {};
  const org = r.organization || {};
  const mgr = r.manager || {};
  const camp = r.campaign || {};
  const debrief = camp.debrief_report || {};
  const stats = camp.campaign_stats || {};

  const status = r.status || 'unknown';
  const statusColor = {
    failed: '#dc2626',
    success: '#16a34a',
    completed: '#16a34a',
    pending: '#a16207',
    processing: '#2563eb',
    queued: '#64748b',
    unknown: '#6b7280',
  }[status] || '#6b7280';

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');
  const managerName = (mgr.first_name || mgr.last_name) ? `${mgr.first_name || ''} ${mgr.last_name || ''}`.trim() : '—';

  function copyToClipboard(text) {
    try { if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text); } catch (e) { /* ignore */ }
  }

  const PrettyJSON = ({ title, obj }) => {
    const text = JSON.stringify(obj ?? {}, null, 2);
    return (
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => copyToClipboard(text)} style={{ border: 'none', background: '#0b5fff', color: '#fff', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Copy</button>
          </div>
        </div>
        <Code>{text}</Code>
      </Section>
    );
  };

  const isLoaded = !!r.id;

  return (
    <Page>
      <TopBar>
        <Link to="../list">← Back to list</Link>
      </TopBar>

      <Header>
        <div>
          <Title>Report Details</Title>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <Sub style={{ marginRight: 12 }}>Tracking: {r.tracking_code || '—'}</Sub>
            <Sub style={{ marginRight: 8 }}>Report id: <small style={{ color: '#9ca3af' }}>{r.id || '—'}</small></Sub>
            <Sub>Org id: <small style={{ color: '#9ca3af' }}>{org.id ?? '-'}</small></Sub>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right', color: '#6b7280', fontSize: 12 }}>
            <div>{managerName} <small style={{ color: '#9ca3af' }}>#{mgr.id ?? '-'}</small></div>
            <div style={{ marginTop: 4 }}>Org: {org.name || '-'} <small style={{ color: '#9ca3af' }}>#{org.id ?? '-'}</small></div>
          </div>
          <Badge style={{ backgroundColor: statusColor }}>{status}</Badge>
        </div>
      </Header>

      {!isLoaded ? (
        <Card>Loading report…</Card>
      ) : (
        <>
          {/* Diagnostics banner */}
          {r.generation_time_minutes && r.generation_time_minutes > 60 && (
            <Card style={{ borderColor: '#fca5a5', background: '#fff7f7' }}>
              <div style={{ fontWeight: 700, color: '#b91c1c' }}>Slow generation notice</div>
              <div style={{ color: '#6b7280', marginTop: 6 }}>This report took {Math.round(r.generation_time_minutes)} minutes to generate — this can indicate heavy processing or an issue to investigate.</div>
            </Card>
          )}

          {/* Developer Notes */}
          <Card style={{ borderColor: '#fbbf24', background: '#fffbeb' }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8 }}>🔍 Developer Notes — Report Status Checks</div>
            <div style={{ color: '#78350f', fontSize: 13, lineHeight: '1.6' }}>
              <div style={{ marginBottom: 6 }}><strong>Current status:</strong> {status}</div>
              {status === 'pending' && <div style={{ color: '#d97706' }}>⚠️ Report pending — waiting for generation to start</div>}
              {status === 'processing' && <div style={{ color: '#2563eb' }}>ℹ️ Report processing — check generation_log for progress</div>}
              {status === 'failed' && <div style={{ color: '#dc2626' }}>⚠️ Report generation failed — check generation_log for errors</div>}
              {status === 'completed' && r.requires_review && !r.reviewed_at && <div style={{ color: '#d97706' }}>⚠️ Report requires review but not yet reviewed — needs approval before sending</div>}
              {status === 'completed' && !r.sent_at && <div style={{ color: '#d97706' }}>⚠️ Report generated but not sent — check scheduled_send_date: {formatDate(r.scheduled_send_date)}</div>}
              {r.sent_at && !r.email_opened_at && <div style={{ color: '#2563eb' }}>ℹ️ Email sent but not opened yet — sent at {formatDate(r.sent_at)}</div>}
              {r.email_opened_at && !r.report_viewed_at && <div style={{ color: '#2563eb' }}>ℹ️ Email opened but report not viewed — opened at {formatDate(r.email_opened_at)}</div>}
              {camp.current_phase === 'report_generation' && status === 'failed' && <div style={{ color: '#dc2626' }}>⚠️ Campaign stuck in report_generation phase due to failed report</div>}
            </div>
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#92400e' }}>📖 View validation guide</summary>
              <div style={{ marginTop: 8, padding: 12, background: '#fff', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
                <div><strong>Expected Status Flow:</strong></div>
                <div>pending → generating → generated → approved → sent</div>
                <div style={{ marginTop: 8 }}><strong>Red Flags:</strong></div>
                <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                  <li>requires_review=true but reviewed_by=null and status=sent</li>
                  <li>status=sent but sent_at=null</li>
                  <li>status=failed and generation_time &gt; 60 minutes</li>
                </ul>
              </div>
            </details>
          </Card>

          <Card>
            <SectionTitle>Report Overview</SectionTitle>
            <Grid>
              <Item>
                <Label>Report ID</Label>
                <Value mono>{r.id}</Value>
              </Item>
              <Item>
                <Label>Type</Label>
                <Value cap>{r.report_type || '—'}</Value>
              </Item>
              <Item>
                <Label>Name</Label>
                <Value>{r.report_name || '—'}</Value>
              </Item>
              <Item>
                <Label>Language</Label>
                <Value>{r.language || '—'}</Value>
              </Item>

              <Item>
                <Label>Organization</Label>
                <Value>
                  {org.name || '—'}
                  {org.id ? <Small> (ID: {org.id})</Small> : null}
                </Value>
              </Item>
              <Item>
                <Label>Manager</Label>
                <Value>
                  {managerName}
                  {mgr.email ? <Small> — {mgr.email}</Small> : null}
                </Value>
              </Item>
              <Item>
                <Label>Requires Review</Label>
                <Value>{r.requires_review ? 'Yes' : 'No'}</Value>
              </Item>
              <Item>
                <Label>Generation Time (min)</Label>
                <Value>{r.generation_time_minutes ?? '—'}</Value>
              </Item>
            </Grid>
          </Card>

          {/* Campaign Info */}
          <Card>
            <SectionTitle>Campaign: {camp.name || '—'}</SectionTitle>
            <Grid>
              <Item>
                <Label>Campaign ID</Label>
                <Value mono>{camp.id || '—'}</Value>
              </Item>
              <Item>
                <Label>Status / Phase</Label>
                <Value cap>{camp.status || '—'} / {camp.current_phase || '—'}</Value>
              </Item>
              <Item>
                <Label>Start Date</Label>
                <Value>{formatDate(camp.start_date)}</Value>
              </Item>
              <Item>
                <Label>End Date</Label>
                <Value>{formatDate(camp.end_date)}</Value>
              </Item>
              <Item>
                <Label>Created By</Label>
                <Value>{camp.created_by || '—'}</Value>
              </Item>
              <Item>
                <Label>Report Generation Date</Label>
                <Value>{formatDate(camp.report_generation_date)}</Value>
              </Item>
              <Item>
                <Label>Action Planning Launch</Label>
                <Value>{formatDate(camp.action_planning_launch_date)}</Value>
              </Item>
              <Item>
                <Label>Check-ins</Label>
                <Value>
                  {camp.total_check_ins_planned ?? 0} planned, every {camp.check_in_frequency_days ?? 30} days
                </Value>
              </Item>
            </Grid>

            {/* Campaign Stats */}
            {Object.keys(stats).length > 0 && (
              <>
                <Divider />
                <SubTitle>Campaign Stats</SubTitle>
                <Grid>
                  <Item>
                    <Label>Total Managers</Label>
                    <Value>{stats.total_managers ?? 0}</Value>
                  </Item>
                  <Item>
                    <Label>Total Groups</Label>
                    <Value>{stats.total_groups ?? 0}</Value>
                  </Item>
                  <Item>
                    <Label>Reports Generated</Label>
                    <Value>{stats.reports_generated ?? 0}</Value>
                  </Item>
                  <Item>
                    <Label>Reports Sent</Label>
                    <Value>{stats.reports_sent ?? 0}</Value>
                  </Item>
                  <Item>
                    <Label>Pending Review</Label>
                    <Value>{stats.reports_pending_review ?? 0}</Value>
                  </Item>
                  <Item>
                    <Label>Events Scheduled / Active / Completed</Label>
                    <Value>
                      {stats.events_scheduled ?? 0} / {stats.events_active ?? 0} / {stats.events_completed ?? 0}
                    </Value>
                  </Item>
                  <Item>
                    <Label>Actions Total / Completed / At Risk</Label>
                    <Value>
                      {stats.actions_total ?? 0} / {stats.actions_completed ?? 0} / {stats.actions_at_risk ?? 0}
                    </Value>
                  </Item>
                  <Item>
                    <Label>Avg Progress</Label>
                    <Value>{stats.avg_progress ?? 0}%</Value>
                  </Item>
                </Grid>
              </>
            )}
          </Card>

          {/* Debrief Report */}
          {debrief.id && (
            <Card>
              <SectionTitle>Debrief Report (ID: {debrief.id})</SectionTitle>
              <Grid>
                <Item>
                  <Label>Report Name</Label>
                  <Value>{debrief.report_name || '—'}</Value>
                </Item>
                <Item>
                  <Label>Org Template</Label>
                  <Value>{debrief.org_template_name || '—'}</Value>
                </Item>
                <Item>
                  <Label>Type</Label>
                  <Value>{debrief.report_type_display || debrief.report_type || '—'}</Value>
                </Item>
                <Item>
                  <Label>Template (Global / Org)</Label>
                  <Value>
                    {debrief.global_template_name || '—'}
                  </Value>
                </Item>
          
                <Item>
                  <Label>Created</Label>
                  <Value>{formatDate(debrief.created_at)}</Value>
                </Item>
                <Item>
                  <Label>Updated</Label>
                  <Value>{formatDate(debrief.updated_at)}</Value>
                </Item>
              </Grid>

              {/* Survey Data Config */}
              {debrief.survey_data && (
                <>
                  <Divider />
                  <SubTitle>Survey Data Config</SubTitle>
                  <PrettyJSON title="Survey Data Sections" obj={debrief.survey_data.sections} />
                  <PrettyJSON title="Include Data" obj={debrief.survey_data.includeData} />
                </>
              )}

              {/* Params */}
              {debrief.params && Object.keys(debrief.params).length > 0 && (
                <>
                  <Divider />
                  <SubTitle>Template params / branding</SubTitle>
                  <PrettyJSON title="Params.additional_params" obj={debrief.params?.additional_params || {}} />
                  <Grid>
                    <Item>
                      <Label>JS URL</Label>
                      <Value mono style={{ fontSize: 11, wordBreak: 'break-all' }}>
                        {debrief.params.js_url || '—'}
                      </Value>
                    </Item>
                    <Item>
                      <Label>CSS URL</Label>
                      <Value mono style={{ fontSize: 11, wordBreak: 'break-all' }}>
                        {debrief.params.css_url || '—'}
                      </Value>
                    </Item>
                  </Grid>
                </>
              )}

              {/* Download Files */}
              {Array.isArray(debrief.download_files) && debrief.download_files.length > 0 && (
                <>
                  <Divider />
                  <SubTitle>Download Files</SubTitle>
                  <PrettyJSON title="Files" obj={debrief.download_files} />
                </>
              )}
            </Card>
          )}

          {/* Actions */}
          <Actions>
            {status === 'failed' && (
              <ActionBtn 
                variant="danger" 
                onClick={handleRetryGeneration}
                disabled={retrying}
              >
                {retrying ? 'Retrying...' : 'Retry Generation'}
              </ActionBtn>
            )}
            {Array.isArray(r.download_files) && r.download_files.length > 0 && (
              <ActionBtn as="a" href={r.download_files[0]?.url || '#'} target="_blank" rel="noreferrer">
                Download Report
              </ActionBtn>
            )}
            {Array.isArray(debrief.download_files) && debrief.download_files.length > 0 && (
              <ActionBtn as="a" href={debrief.download_files[0]?.url || '#'} target="_blank" rel="noreferrer">
                Download Debrief
              </ActionBtn>
            )}
          </Actions>

          {/* Additional JSON Blocks */}
          <PrettyJSON title="Report Params" obj={r.params} />
          <PrettyJSON title="Report Data" obj={r.report_data} />
          <PrettyJSON title="Source Survey Data" obj={r.source_survey_data} />
          <PrettyJSON title="Actionable Insights" obj={r.actionable_insights} />
          <PrettyJSON title="Key Metrics" obj={r.key_metrics} />
          <PrettyJSON title="Template Config" obj={r.template_config} />
          <PrettyJSON title="Generation Log" obj={r.generation_log} />

          <Section>
            <details>
              <summary>Email</summary>
              <Grid>
                <Item>
                  <Label>Subject</Label>
                  <Value>{r.email_subject || '—'}</Value>
                </Item>
                <Item full>
                  <Label>Content</Label>
                  <Code>{r.email_content || ''}</Code>
                </Item>
                <Item>
                  <Label>Scheduled Send</Label>
                  <Value>{formatDate(r.scheduled_send_date)}</Value>
                </Item>
                <Item>
                  <Label>Sent At</Label>
                  <Value>{formatDate(r.sent_at)}</Value>
                </Item>
                <Item>
                  <Label>Email Opened</Label>
                  <Value>{formatDate(r.email_opened_at)}</Value>
                </Item>
                <Item>
                  <Label>Report Viewed</Label>
                  <Value>{formatDate(r.report_viewed_at)}</Value>
                </Item>
              </Grid>
            </details>
          </Section>
        </>
      )}
    </Page>
  );
}


/* styled */
const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
`;

const Sub = styled.div`
  color: #6b7280;
  font-size: 12px;
`;

const Badge = styled.span`
  color: white;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  text-transform: capitalize;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const SubTitle = styled.h4`
  margin: 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 16px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 12px 16px;
`;

const Item = styled.div`
  grid-column: ${(p) => (p.full ? '1 / -1' : 'span 6')};
  min-width: 0;
`;

const Label = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
`;

const Value = styled.div`
  font-size: 14px;
  ${(p) => p.mono ? 'font-family: ui-monospace, SFMono-Regular, Menlo, monospace;' : ''}
  ${(p) => p.cap ? 'text-transform: capitalize;' : ''}
  word-break: break-word;
`;

const Small = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

const Section = styled(Card)`
  padding: 8px 12px;
`;

const Code = styled.pre`
  background: #0b1020;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 6px;
  overflow: auto;
  font-size: 12px;
  margin: 8px 0 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionBtn = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.variant === 'danger' ? '#dc2626' : '#2563eb'};
  color: ${(p) => p.variant === 'danger' ? '#dc2626' : '#2563eb'};
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    background: ${(p) => p.variant === 'danger' ? '#fef2f2' : '#eff6ff'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;